import type { HintCode, HintSeverity, IntegrationHint } from '../../types/integrations';
import { HINT_PRESENTATION, iconForHint } from '../../lib/integration-hints';

interface HintBannerProps {
  code: HintCode;
  hint: IntegrationHint;
}

const SEVERITY_STYLES: Record<
  HintSeverity,
  { container: string; icon: string; title: string; body: string; meta: string }
> = {
  info: {
    container: 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-800 dark:text-blue-200',
    body: 'text-blue-800 dark:text-blue-200',
    meta: 'text-blue-700 dark:text-blue-300',
  },
  warning: {
    container: 'border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20',
    icon: 'text-yellow-600 dark:text-yellow-400',
    title: 'text-yellow-800 dark:text-yellow-200',
    body: 'text-yellow-800 dark:text-yellow-200',
    meta: 'text-yellow-700 dark:text-yellow-300',
  },
  error: {
    container: 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-800 dark:text-red-200',
    body: 'text-red-800 dark:text-red-200',
    meta: 'text-red-700 dark:text-red-300',
  },
};

export default function HintBanner({ code, hint }: HintBannerProps) {
  const presentation = HINT_PRESENTATION[code];
  const Icon = iconForHint(code, hint.severity);
  const styles = SEVERITY_STYLES[hint.severity];

  const firstSeen = new Date(hint.firstSeenAt);
  const lastSeen = new Date(hint.lastSeenAt);
  const showLastSeen = lastSeen.getTime() - firstSeen.getTime() > 60_000;

  return (
    <div className={`rounded-lg border p-4 flex gap-3 ${styles.container}`}>
      <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${styles.icon}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${styles.title}`}>
          {presentation?.title ?? code}
        </p>
        <p className={`text-sm mt-1 ${styles.body}`}>{hint.message}</p>
        {presentation?.remediation && (
          <p className={`text-xs mt-2 ${styles.meta}`}>{presentation.remediation}</p>
        )}
        <p className={`text-xs mt-2 ${styles.meta}`}>
          Since {firstSeen.toLocaleString()}
          {showLastSeen && <> · Last seen {lastSeen.toLocaleString()}</>}
        </p>
      </div>
    </div>
  );
}
